/**
 * HierarchyTreeView — virtualized parent/is_group tree for hierarchical catalogs.
 *
 * Shared by App RuntimeListView (API-backed via useHierarchyTree) and Designer
 * PredefinedTab (in-memory entries). Group rows show chevron + kind icon + all
 * column values; leaf rows use ListDataRow.
 */
import React, { useEffect, useRef } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PrintIcon from '@mui/icons-material/Print'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import RestoreIcon from '@mui/icons-material/Restore'
import { useVirtualizer } from '@tanstack/react-virtual'
import { renderListCell, monoSx } from '@/shared/listCell'
import { HierarchyKindIcon } from '@/shared/HierarchyKindIcon'
import type { ListColumnSpec } from '@/runtime/listSchema'
import { ListRowOverflow, LIST_ROW_OVERFLOW_COL_WIDTH, type ListRowOverflowItem } from '@/shared/listTable'
import { isNumericListColumn, type ListCellAttrHint } from '@/shared/formatListCellValue'
import { ListDataRow, type ListRowPrint } from '@/runtime/ListDataRow'
import { ListHeaderCell } from '@/runtime/ListHeaderCell'
import { isPredefinedRecord } from '@/runtime/appHiddenAttributes'
import type { HierarchyEntry } from './hierarchyTypes'
import { listCellHairlineSx, listDensityCellSx, listDensityControlSx, listHeaderStickySx, listNumericBodySx } from '@/shared/listTable/listTableChrome'
import { useDensity } from '@/shared/density/DensityProvider'
import { ListEmptyState } from './ListEmptyState'

/** Horizontal indent per tree depth level, matching the spec's `depth * 16px`. */
const INDENT_PX = 16

function HierarchyGroupRowActions(props: {
  row: Record<string, unknown>
  print: ListRowPrint
  hideDelete: boolean
  onEdit: (row: Record<string, any>) => void
  onDelete: (recordId: string, wasMarked?: boolean) => void
  onUndelete: (recordId: string) => void
}) {
  const { row, print, hideDelete, onEdit, onDelete, onUndelete } = props
  const kebabRef = React.useRef<HTMLButtonElement>(null)
  const isDeleted = row.is_deleted === true || row.is_deleted === 1
  // A group row's click selects/expands it, so opening the group itself lives here.
  const items: ListRowOverflowItem[] = [
    { id: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => onEdit(row) },
  ]
  if (print.hasPrintLayouts && row.id) {
    const fire = (kind: 'print' | 'pdf') => {
      const el = kebabRef.current
      if (!el) return
      print.openLayoutChoice(el, row, kind)
    }
    items.push({
      id: 'print',
      label: 'Print',
      icon: <PrintIcon fontSize="small" />,
      disabled: print.printLoading,
      onClick: () => fire('print'),
    })
    items.push({
      id: 'pdf',
      label: 'PDF',
      icon: <PictureAsPdfIcon fontSize="small" />,
      disabled: print.printLoading,
      onClick: () => fire('pdf'),
    })
  }
  if (isDeleted) {
    items.push({
      id: 'restore',
      label: 'Restore',
      icon: <RestoreIcon fontSize="small" />,
      color: 'success',
      onClick: () => onUndelete(String(row.id)),
    })
  } else if (!hideDelete) {
    items.push({
      id: 'delete',
      label: 'Delete',
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () =>
        onDelete(
          String(row.id),
          row.is_deleted === true || row.is_deleted === 1,
        ),
    })
  }
  return <ListRowOverflow buttonRef={kebabRef} items={items} />
}

export interface HierarchyTreeViewProps {
  entries: HierarchyEntry[]
  columns: ListColumnSpec[]
  hintFor: (col: ListColumnSpec) => ListCellAttrHint | undefined
  rowHeight: number
  toggle: (groupId: string) => void
  print: ListRowPrint
  onEdit: (row: Record<string, any>) => void
  onDelete: (recordId: string, wasMarked?: boolean) => void
  onUndelete: (recordId: string) => void
  loading?: boolean
  error?: string | null
  /** Highlights the selected record (Create-menu parent seeding). */
  selectedId?: string | null
  onSelect?: (row: Record<string, any>) => void
  /** Header sort/resize/reorder — mirrors the flat list's `ListHeaderCell` wiring. */
  sortField: string
  sortDir: 'asc' | 'desc'
  onSort: (field: string) => void
  onResize: (name: string, width: number) => void
  onResetWidth: (name: string) => void
  onReorder: (from: string, to: string) => void
  /**
   * Column hosting kind icons + indent. App passes the first visible column;
   * falls back to `columns[0]` when missing/unknown.
   */
  kindIconColumn?: string | null
  /**
   * When true, Delete stays available for predefined rows (Designer Predefined
   * authoring). App leaves this false so `isPredefinedRecord` still hides Delete.
   */
  allowDeletePredefined?: boolean
  /** Effective delete permission (Task 12). Defaults to true — see ListDataRow. */
  canDelete?: boolean
  /** Hide sort/resize headers (Designer authoring — columns are fixed). */
  headerInteractive?: boolean
  /** Selection column (mirrors FlatListView). */
  selectable?: boolean
  isSelected?: (rowId: string) => boolean
  onToggleSelect?: (e: React.MouseEvent, index: number) => void
  selectAllChecked?: boolean
  selectAllIndeterminate?: boolean
  onToggleSelectAll?: () => void
}

export const HierarchyTreeView: React.FC<HierarchyTreeViewProps> = ({
  entries,
  columns,
  hintFor,
  rowHeight,
  toggle,
  print,
  onEdit,
  onDelete,
  onUndelete,
  loading,
  error,
  selectedId,
  onSelect,
  sortField,
  sortDir,
  onSort,
  onResize,
  onResetWidth,
  onReorder,
  kindIconColumn,
  allowDeletePredefined = false,
  canDelete = true,
  headerInteractive = true,
  selectable = false,
  isSelected,
  onToggleSelect,
  selectAllChecked = false,
  selectAllIndeterminate = false,
  onToggleSelectAll,
}) => {
  const { density, tokens } = useDensity()
  const cellDensitySx = listDensityCellSx(tokens)
  const controlDensitySx = listDensityControlSx(density)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const iconColumnName = kindIconColumn && columns.some((c) => c.name === kindIconColumn)
    ? kindIconColumn
    : columns[0]?.name ?? null

  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  })

  useEffect(() => {
    virtualizer.measure()
  }, [rowHeight, virtualizer])

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0
  const colSpan = columns.length + 1 + (selectable ? 1 : 0)

  const hideDelete = (row: Record<string, unknown>) =>
    (isPredefinedRecord(row) && !allowDeletePredefined) || !canDelete

  const renderGroupLabel = (entry: Extract<HierarchyEntry, { kind: 'group' }>): React.ReactNode => {
    const col = (iconColumnName && columns.find((c) => c.name === iconColumnName)) || columns[0]
    if (col) return renderListCell(col, entry.record, hintFor(col))
    const raw = entry.record.name ?? entry.record.code ?? entry.record.predefined_name
    return raw == null || raw === '' ? '—' : String(raw)
  }

  const renderGroupRow = (entry: Extract<HierarchyEntry, { kind: 'group' }>, key: React.Key, index: number) => {
    const row = entry.record
    const isDeleted = row.is_deleted === true || row.is_deleted === 1
    const predefined = isPredefinedRecord(row)
    return (
      <TableRow
        key={key}
        hover
        selected={selectedId != null && String(selectedId) === entry.id}
        onClick={() => onSelect?.(row)}
        sx={{
          cursor: 'pointer',
          bgcolor: 'action.hover',
          height: rowHeight,
          opacity: isDeleted ? 0.5 : 1,
          textDecoration: isDeleted ? 'line-through' : 'none',
        }}
      >
        {selectable && (
          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ ...cellDensitySx, ...listCellHairlineSx }}>
            <Checkbox
              size="small"
              sx={controlDensitySx}
              checked={isSelected?.(entry.id) ?? false}
              onClick={(e) => onToggleSelect?.(e, index)}
              slotProps={{ input: { 'aria-label': 'Select row' } }}
            />
          </TableCell>
        )}
        {columns.map((col) => {
          if (col.name === iconColumnName) {
            return (
              <TableCell key={col.name} sx={{ pl: 1 + entry.depth * (INDENT_PX / 8), ...cellDensitySx, ...listCellHairlineSx }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    sx={controlDensitySx}
                    aria-expanded={entry.expanded}
                    aria-label={entry.expanded ? 'Collapse group' : 'Expand group'}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggle(entry.id)
                    }}
                  >
                    {entry.expanded ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </IconButton>
                  <HierarchyKindIcon kind="group" expanded={entry.expanded} locked={predefined} />
                  <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
                    {renderGroupLabel(entry)}
                  </Typography>
                  {entry.childCount != null && (
                    <Chip label={entry.childCount} size="small" sx={{ height: 20, fontSize: 11 }} />
                  )}
                </Box>
              </TableCell>
            )
          }
          return (
            <TableCell key={col.name} sx={{ ...cellDensitySx, ...(col.mono ? monoSx : {}), ...listCellHairlineSx, ...listNumericBodySx(isNumericListColumn(hintFor(col))) }}>
              {renderListCell(col, row, hintFor(col))}
            </TableCell>
          )
        })}
        <TableCell className="list-row-actions" align="right" sx={{ width: LIST_ROW_OVERFLOW_COL_WIDTH, ...cellDensitySx, ...listCellHairlineSx }} onClick={(e) => e.stopPropagation()}>
          <HierarchyGroupRowActions
            row={row}
            print={print}
            hideDelete={hideDelete(row)}
            onEdit={onEdit}
            onDelete={onDelete}
            onUndelete={onUndelete}
          />
        </TableCell>
      </TableRow>
    )
  }

  const renderRowEntry = (entry: Extract<HierarchyEntry, { kind: 'row' }>, key: React.Key, index: number) => (
    <ListDataRow
      key={key}
      row={entry.record}
      columns={columns}
      hintFor={hintFor}
      rowHeight={rowHeight}
      print={print}
      onEdit={onEdit}
      onDelete={onDelete}
      onUndelete={onUndelete}
      selectable={selectable}
      selected={isSelected?.(entry.id)}
      rowIndex={index}
      onToggleSelect={onToggleSelect}
      depth={entry.depth}
      kindIconColumn={iconColumnName}
      leading={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Element hierarchy: a plain element with its own children (per the
              parent-summary) gets a real expander, same as a group row's.
              Leaves keep the invisible twin so the document glyph still
              lines up under the parent's folder/chevron column. */}
          {entry.hasChildren ? (
            <IconButton
              size="small"
              aria-expanded={entry.expanded}
              aria-label={entry.expanded ? 'Collapse' : 'Expand'}
              onClick={(e) => {
                e.stopPropagation()
                toggle(entry.id)
              }}
            >
              {entry.expanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <IconButton size="small" tabIndex={-1} aria-hidden sx={{ visibility: 'hidden', pointerEvents: 'none' }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
          <HierarchyKindIcon kind="element" locked={isPredefinedRecord(entry.record)} />
        </Box>
      }
      allowDeletePredefined={allowDeletePredefined}
      canDelete={canDelete}
    />
  )

  const renderSkeletonRow = (key: React.Key) => (
    <TableRow key={key} sx={{ height: rowHeight }}>
      {selectable && <TableCell padding="checkbox" sx={listCellHairlineSx} />}
      {columns.map((col) => (
        <TableCell key={col.name} sx={listCellHairlineSx}>
          <Skeleton variant="text" width="80%" />
        </TableCell>
      ))}
      <TableCell align="right" sx={listCellHairlineSx}>
        <Skeleton variant="text" width="60%" />
      </TableCell>
    </TableRow>
  )

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, gap: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ flex: 1, overflow: 'auto' }}
        ref={scrollRef as React.Ref<HTMLDivElement>}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox" sx={{ ...listHeaderStickySx, ...listCellHairlineSx }}>
                  <Checkbox
                    size="small"
                    indeterminate={selectAllIndeterminate}
                    checked={selectAllChecked}
                    onChange={onToggleSelectAll}
                    slotProps={{ input: { 'aria-label': 'Select all rows' } }}
                  />
                </TableCell>
              )}
              {columns.map((col) =>
                headerInteractive ? (
                  <ListHeaderCell
                    key={col.name}
                    col={col}
                    sortActive={sortField === col.name}
                    sortDir={sortDir}
                    onSort={() => onSort(col.name)}
                    onResize={onResize}
                    onResetWidth={onResetWidth}
                    onReorder={onReorder}
                  />
                ) : (
                  <TableCell key={col.name} sx={{ ...listHeaderStickySx, ...listCellHairlineSx }}>
                    {col.title || col.name}
                  </TableCell>
                ),
              )}
              <TableCell
                align="right"
                aria-label="Row actions"
                sx={{ width: LIST_ROW_OVERFLOW_COL_WIDTH, ...listHeaderStickySx, ...listCellHairlineSx, textAlign: 'right' }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && entries.length === 0 ? (
              Array.from({ length: 4 }, (_, i) => renderSkeletonRow(`s-${i}`))
            ) : (
              <>
                {paddingTop > 0 && (
                  <TableRow style={{ height: paddingTop }}>
                    <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }} />
                  </TableRow>
                )}

                {virtualItems.map((virtualRow) => {
                  const entry = entries[virtualRow.index]
                  if (!entry) return null
                  if (entry.kind === 'group') {
                    return renderGroupRow(entry, `g-${virtualRow.key}`, virtualRow.index)
                  }
                  return renderRowEntry(entry, `r-${virtualRow.key}-${entry.id}`, virtualRow.index)
                })}

                {paddingBottom > 0 && (
                  <TableRow style={{ height: paddingBottom }}>
                    <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }} />
                  </TableRow>
                )}

                {!loading && entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center" sx={{ py: 0, border: 0 }}>
                      <ListEmptyState />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
